import { Fragment, useEffect, useMemo, useState } from 'react';
import { readStored, writeStored } from '@/components/desktop';
import Login from '@/screens/Login';
import { NotBuiltYet } from '@/screens/Inventory';
import { Adjustments } from '@/screens/Adjustments';
import { Demands } from '@/screens/Demands';
import { ConsumptionLog } from '@/screens/Consumption';
import { InwardShipments } from '@/screens/Inward';
import { Reference } from '@/screens/Reference';
import { Transfers } from '@/screens/Transfers';
import { Workbench } from '@/screens/Workbench';
import { Announcements } from '@/screens/Announcements';
import { Audit } from '@/screens/Audit';
import { Calendar } from '@/screens/Calendar';
import { NeedsAttention } from '@/screens/NeedsAttention';
import { DailyRegister } from '@/screens/DailyRegister';
import { Dispatch } from '@/screens/sales/Dispatch';
import { OtherSales } from '@/screens/sales/OtherSales';
import { Billing } from '@/screens/finance/Billing';
import { CashBook } from '@/screens/finance/CashBook';
import { Expenses } from '@/screens/finance/Expenses';
import { FinanceDashboard } from '@/screens/finance/FinanceDashboard';
import { FinanceReports } from '@/screens/finance/FinanceReports';
import { FinanceSetup } from '@/screens/finance/FinanceSetup';
import { JournalVouchers } from '@/screens/finance/JournalVouchers';
import { Receipts } from '@/screens/finance/Receipts';
import { Dashboard } from '@/screens/dashboard/Dashboard';
import { FarmDashboard } from '@/screens/FarmDashboard';
import { FarmManager } from '@/screens/FarmManager';
import { Flocks } from '@/screens/Flocks';
import { AccessHelpers } from '@/screens/iam/AccessHelpers';
import { MyPeople } from '@/screens/iam/MyPeople';
import { Roles } from '@/screens/iam/Roles';
import { Users } from '@/screens/iam/Users';
import { Advances } from '@/screens/hr/Advances';
import { Attendance } from '@/screens/hr/Attendance';
import { Employees } from '@/screens/hr/Employees';
import { HrDashboard } from '@/screens/hr/HrDashboard';
import { HrReports } from '@/screens/hr/HrReports';
import { HrSetup } from '@/screens/hr/HrSetup';
import { Leave } from '@/screens/hr/Leave';
import { MyPay } from '@/screens/hr/MyPay';
import { Payroll } from '@/screens/hr/Payroll';
import { Settlements } from '@/screens/hr/Settlements';
import { PasswordPage, Settings } from '@/screens/Settings';
import { openSettingsPage } from '@/screens/settings/pages';
import { CommandPalette } from '@/shell/CommandPalette';
import { ConfirmHost } from '@/shell/ConfirmHost';
import { SavedOfflineHost } from '@/shell/SavedOfflineHost';
import { ConfirmItsYouHost } from '@/shell/ConfirmItsYou';
import { ErrorBoundary } from '@/shell/ErrorBoundary';
import { HelpDialog } from '@/shell/HelpDialog';
import { LockScreen } from '@/shell/LockScreen';
import { Shell } from '@/shell/Shell';
import { Toaster } from '@/shell/Toaster';
import { signOutSafely } from '@/shell/actions';
import { MODULES, canSee, firstAllowedScreen, resolveScreenId } from '@/shell/modules';
import { useAuth } from '@/stores/auth';
import { useCompany } from '@/stores/company';
import { confirmDiscard } from '@/stores/confirm';
import { useIdleLock, useLock } from '@/stores/lock';
import { usePreferences } from '@/stores/preferences';
import { useShellUi } from '@/stores/shellUi';
import { discardUnsavedWork, hasUnsavedWork, useUnsavedCloseWarning } from '@/stores/unsaved';
import {
  intentFromQuery,
  intentToQuery,
  useShellNav,
  type ScreenIntent,
} from '@/stores/shellNav';

/**
 * A screen opened with "Open in new window" turned on. Reached by
 * `?screen=<id>&standalone=1`, which is all `window.open` needs to give — the
 * new window is a normal same-origin page, so it authenticates itself from the
 * httpOnly refresh cookie exactly like the main window does on a reload.
 */
function standaloneScreenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('standalone') === '1' ? params.get('screen') : null;
}

const ACTIVE_SCREEN_KEY = 'poultry.shell.activeScreen';

/** Whether `id` resolves to a real, built screen this caller can currently see. */
function isReachable(id: string, can: (permission: string) => boolean): boolean {
  const resolved = resolveScreenId(id);
  // Settings lives outside the module map — its own rail button, not a
  // module screen — so it has no ScreenDef to look up here.
  if (resolved === 'settings') return true;
  const def = MODULES.flatMap((m) => m.screens).find((s) => s.id === resolved);
  return Boolean(def?.built && canSee(def, can));
}

export default function App() {
  const { status, user, restore, can } = useAuth();
  const hydratePreferences = usePreferences((s) => s.hydrate);
  const windowMode = usePreferences((s) => s.prefs.windowMode);
  const idleLockMinutes = useCompany((s) => s.company.idleLockMinutes);
  const companyName = useCompany((s) => s.company.nameEn);
  const locked = useLock((s) => s.locked);
  const lock = useLock((s) => s.lock);
  const dialog = useShellUi((s) => s.dialog);
  const openDialog = useShellUi((s) => s.open);
  const closeDialog = useShellUi((s) => s.close);
  // null until the person navigates; until then the last screen they were on
  // is read below, once the session (and so whose layout) is known.
  const [screen, setScreen] = useState<string | null>(null);
  // Bumped on every navigation that carries an intent, so the target screen
  // remounts and reads it even when it is already the active screen.
  const [intentVisit, setIntentVisit] = useState(0);

  // Read once: a standalone window is single-purpose and never re-navigates
  // itself, so this does not need to track further URL changes.
  const standaloneId = useMemo(() => {
    const id = standaloneScreenFromUrl();
    const intent = id ? intentFromQuery(new URLSearchParams(window.location.search)) : null;
    if (id && intent) useShellNav.setState({ pendingIntent: { screenId: id, intent } });
    return id;
  }, []);

  // A refresh cookie survives a page reload, so try to resume the session
  // before deciding the user is anonymous.
  useEffect(() => {
    void restore();
  }, [restore]);

  // The server is the source of truth for preferences, so they are applied as
  // soon as the session resolves rather than read from this browser.
  useEffect(() => {
    if (user) hydratePreferences(user.preferences as never);
  }, [user, hydratePreferences]);

  /**
   * With "Open in new window" on, clicking a module opens a real separate
   * browser window instead of navigating this one away. A window name keyed
   * to the screen id means clicking the same module twice focuses the window
   * that is already open rather than spawning a duplicate.
   *
   * Called directly inside the click handler with no `await` before it —
   * popup blockers allow window.open only when it is synchronous with the
   * gesture that triggered it.
   */
  function navigate(id: string, intent?: ScreenIntent) {
    // Settings is about this window and this person, so it always opens in
    // place, even with "Open in a new window" on.
    if (windowMode === 'new' && id !== 'settings') {
      const url = `${window.location.origin}${window.location.pathname}?screen=${id}&standalone=1${intentToQuery(intent)}`;
      window.open(url, `poultry-ms-${id}`, 'noopener');
      return;
    }
    const go = () => {
      if (intent) {
        useShellNav.setState({ pendingIntent: { screenId: id, intent } });
        setIntentVisit((n) => n + 1);
      }
      setScreen(id);
      writeStored(ACTIVE_SCREEN_KEY, id);
    };
    // Moving this window to another screen (or re-opening this one with an
    // intent) throws away an open form, so ask first when it holds typing.
    const leaving = id !== activeScreen(screen, can) || intent !== undefined;
    if (leaving && hasUnsavedWork()) {
      void confirmDiscard().then((ok) => {
        if (!ok) return;
        discardUnsavedWork();
        go();
      });
      return;
    }
    go();
  }

  useUnsavedCloseWarning();
  useIdleLock(idleLockMinutes, Boolean(user));

  // Lets a screen (the hierarchy tree, jumping from a shed node to the Sheds
  // table) trigger the same navigate() the sidebar uses, without threading
  // onNavigate as a prop through Shell into every screen component. Defined
  // above the early returns below so this hook always runs in the same order.
  useEffect(() => {
    useShellNav.setState({ navigate });
  });

  // Keys that work anywhere, as in a desktop application: Ctrl+K find,
  // F1 help, Ctrl+, Settings, Ctrl+Shift+L lock. Re-bound every render so they
  // always call the current navigate(), which knows about new windows.
  useEffect(() => {
    if (!user || locked) return;
    function onKeyDown(e: KeyboardEvent) {
      const ctrl = (e.ctrlKey || e.metaKey) && !e.altKey;
      if (ctrl && !e.shiftKey && e.key === ',') navigate('settings');
      else if (ctrl && !e.shiftKey && e.key.toLowerCase() === 'k') openDialog('palette');
      else if (ctrl && e.shiftKey && e.key.toLowerCase() === 'l') lock();
      else if (e.key === 'F1') openDialog('help');
      else return;
      e.preventDefault();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  // The window title names the screen and the company, like a desktop
  // program's title bar, and tells several open windows apart on the taskbar.
  const titleScreen = user ? screenLabel(standaloneId ?? activeScreen(screen, can)) : 'Sign in';
  useEffect(() => {
    document.title = `${titleScreen} — ${companyName}`;
  }, [titleScreen, companyName]);

  if (status === 'checking') {
    return (
      <main className="boot">
        <p className="muted">Restoring your session…</p>
      </main>
    );
  }

  if (status === 'anonymous' || !user) return <Login />;

  // After a complete wipe the Super Admin is on the default password. The
  // server refuses everything else until it is changed, so the app shows
  // nothing else either.
  if (user.mustChangePassword) return <PasswordDue name={user.fullName} />;

  const overlays = (
    <>
      {dialog === 'palette' && <CommandPalette />}
      {dialog === 'help' && (
        <HelpDialog
          screenLabel={titleScreen}
          onClose={closeDialog}
          onOpenKeyboard={() => {
            closeDialog();
            openSettingsPage('keyboard', navigate);
          }}
        />
      )}
      <ConfirmHost />
      <SavedOfflineHost />
      <ConfirmItsYouHost />
      <Toaster />
      {locked && <LockScreen user={user} onSignOut={() => void signOutSafely()} />}
    </>
  );

  if (standaloneId) {
    return (
      <>
        <div inert={locked} className="app__frame">
          <StandaloneScreen id={standaloneId} />
        </div>
        {overlays}
      </>
    );
  }

  const active = activeScreen(screen, can);

  return (
    <>
      <div inert={locked} className="app__frame">
        <Shell active={active} onNavigate={navigate}>
          <ErrorBoundary
            key={`${active}-${intentVisit}`}
            label={screenLabel(active)}
            onLeave={() => navigate(firstAllowedScreen(can))}
            leaveLabel="Go to my first screen"
          >
            <Fragment>{renderScreen(active)}</Fragment>
          </ErrorBoundary>
        </Shell>
      </div>
      {overlays}
    </>
  );
}

/**
 * The screen this window shows. Derived rather than set from an effect: the
 * landing screen is a function of the user's permissions. Until the person
 * navigates, the last screen they were on is restored (per person, see
 * setStorageUser); if permissions changed since and it is no longer theirs to
 * see, this falls back the same way a first visit does.
 */
function activeScreen(chosen: string | null, can: (permission: string) => boolean): string {
  const screen = chosen ?? readStored<string | null>(ACTIVE_SCREEN_KEY, null);
  return screen && isReachable(screen, can) ? screen : firstAllowedScreen(can);
}

function screenLabel(rawId: string): string {
  const id = resolveScreenId(rawId);
  if (id === 'settings') return 'Settings';
  return MODULES.flatMap((m) => m.screens).find((s) => s.id === id)?.label ?? 'Poultry MS';
}

/** The whole app while a new password is required: that page and signing out. */
function PasswordDue({ name }: { name: string }) {
  return (
    <div className="standalone">
      <header className="standalone__bar">
        <h1 className="standalone__title">Choose a new password — {name}</h1>
        <button type="button" className="btn btn--ghost" onClick={() => void signOutSafely()}>
          Sign out
        </button>
      </header>
      <div className="canvas passworddue">
        <PasswordPage required />
      </div>
    </div>
  );
}

/** A single screen with no sidebar, for a window opened via "Open in new window". */
function StandaloneScreen({ id }: { id: string }) {
  const resolved = resolveScreenId(id);
  const label = screenLabel(resolved);

  return (
    <div className="standalone">
      <header className="standalone__bar">
        <h1 className="standalone__title">{label}</h1>
        <a className="standalone__back" href={window.location.pathname}>
          Open full app
        </a>
      </header>
      <div className="canvas">{renderScreen(id)}</div>
    </div>
  );
}

function renderScreen(rawId: string) {
  const id = resolveScreenId(rawId);
  switch (id) {
    case 'inv-workbench':
      return <Workbench />;
    case 'inv-reference':
      return <Reference />;
    case 'inv-inward':
      return <InwardShipments />;
    case 'inv-xfer':
      return <Transfers />;
    case 'inv-consume':
      return <ConsumptionLog />;
    case 'inv-adjust':
      return <Adjustments />;
    case 'inv-demand':
      return <Demands />;
    case 'home':
      return <Dashboard />;
    case 'home-announce':
      return <Announcements />;
    case 'home-calendar':
      return <Calendar />;
    case 'home-stuck':
      return <NeedsAttention />;
    case 'iam-users':
      return <Users />;
    case 'iam-roles':
      return <Roles />;
    case 'iam-helpers':
      return <AccessHelpers />;
    case 'iam-mypeople':
      return <MyPeople />;
    case 'iam-audit':
      return <Audit />;
    case 'mdm-dash':
      return <FarmDashboard />;
    case 'mdm-farm':
      return <FarmManager />;
    case 'mdm-flock':
      return <Flocks />;
    // The Daily Register, which replaced the Mortality Tracker this id used
    // to name. The id stays so bookmarks and the search keep working.
    case 'ops-mort':
      return <DailyRegister />;
    case 'sd-gate':
      return <Dispatch />;
    case 'sd-bags':
      return <OtherSales />;
    case 'fin-dash':
      return <FinanceDashboard />;
    case 'fin-bill':
      return <Billing />;
    case 'fin-recv':
      return <Receipts />;
    case 'fin-cash':
      return <CashBook />;
    case 'fin-exp':
      return <Expenses />;
    case 'fin-rep':
      return <FinanceReports />;
    case 'fin-jv':
      return <JournalVouchers />;
    case 'fin-setup':
      return <FinanceSetup />;
    case 'settings':
      return <Settings />;
    case 'hr-dash':
      return <HrDashboard />;
    case 'hr-emp':
      return <Employees />;
    case 'hr-att':
      return <Attendance />;
    case 'hr-leave':
      return <Leave />;
    case 'hr-adv':
      return <Advances />;
    case 'hr-pay':
      return <Payroll />;
    case 'hr-settle':
      return <Settlements />;
    case 'hr-rep':
      return <HrReports />;
    case 'hr-setup':
      return <HrSetup />;
    case 'hr-mypay':
      return <MyPay />;
    default: {
      const label =
        MODULES.flatMap((m) => m.screens).find((s) => s.id === id)?.label ?? 'Screen';
      return <NotBuiltYet label={label} />;
    }
  }
}
